<div class="flex h-full min-h-0 flex-col bg-slate-100 text-slate-900">
  <app-sidebar-header (close)="requestClose()" />

  <div class="flex shrink-0 flex-col gap-3 border-b border-slate-200 bg-white px-5 py-4">
    <div class="flex items-center gap-3">
      <div class="min-w-0 flex-1">
        <div class="text-xs font-semibold uppercase tracking-wide text-slate-400">{{ entityTitleKey | translate }}</div>
        @if (loading) {
          <div class="mt-2 h-7 w-72 animate-pulse rounded bg-slate-200"></div>
        } @else if (!isCardLoaded) {
          <div class="mt-1 text-2xl font-semibold leading-tight text-slate-300">—</div>
        } @else if (isFieldEditing('name')) {
          <input
            #editableControl
            class="mt-1 w-full border-0 bg-transparent p-0 text-2xl font-semibold leading-tight text-slate-950 outline-none placeholder:text-slate-300"
            [formControl]="form.controls.name"
            placeholder="{{ 'name' | translate }}"
            (focusout)="closeFieldOnFocusOut($event)"
          />
        } @else {
          <button
            type="button"
            class="mt-1 block w-full cursor-text border-0 bg-transparent p-0 text-left text-2xl font-semibold leading-tight text-slate-950 outline-none"
            data-card-detail-edit-trigger="true"
            (click)="editField('name')"
          >
            {{ getEditableFieldValue('name') }}
          </button>
        }
      </div>

      @if (viewers.length) {
        <app-board-v3-presence-avatars
          class="flex shrink-0 items-center"
          [viewers]="viewers"
          [visibleCount]="4"
          [avatarWrapperClass]="'ring-2 ring-white'"
          [extraClass]="'h-7 min-w-7 bg-slate-200 px-1.5 text-xs text-slate-600'"
        />
      }

      @if (isCardLoaded) {
        <div class="flex items-center gap-2">
          <button
            type="button"
            class="flex items-center justify-center gap-1.5 rounded-md! bg-rose-400 text-sm font-semibold text-white transition hover:bg-rose-500 disabled:cursor-not-allowed disabled:opacity-60 py-1 px-2"
            [disabled]="movingStage"
          >
            <app-icon>cancel</app-icon>
            {{ 'board_v3_mark_failed' | translate }}
          </button>
          <button
            type="button"
            class="flex items-center justify-center gap-1.5 rounded-md! bg-emerald-400 text-sm font-semibold text-white transition hover:bg-emerald-500 disabled:cursor-not-allowed disabled:opacity-60 py-1 px-2"
            [disabled]="movingStage"
          >
            <app-icon>check_circle</app-icon>
            {{ 'board_v3_mark_success' | translate }}
          </button>
        </div>
      }

      @if (canEditSections && activeDetailTab === CardDetailTab.GENERAL) {
        <button
          type="button"
          class="inline-flex h-9 w-9 shrink-0 items-center justify-center rounded-lg! border border-slate-200 bg-white text-slate-600 transition hover:border-orange-300 hover:bg-orange-50 hover:text-orange-600 disabled:pointer-events-none disabled:border-slate-100 disabled:bg-slate-50 disabled:text-slate-300"
          [matTooltip]="'edit_sections' | translate"
          matTooltipPosition="below"
          [disabled]="sectionEditMode"
          (click)="enterSectionEditMode()"
        >
          <app-icon class="!h-5 !w-5 !text-[20px] !leading-5">tune</app-icon>
        </button>
      }
    </div>

    @if (isCardLoaded && columns.length) {
      <div class="flex flex-wrap gap-2">
        @for (stage of columns; track stage.id) {
          <button
            type="button"
            class="min-h-5 flex-1 basis-32 rounded-md! px-2 py-1 text-left text-sm font-medium transition disabled:cursor-not-allowed"
            [style.background-color]="isStageReached(stage) ? selectedStage?.color?.background : '#eef1f4'"
            [style.color]="isStageReached(stage) ? selectedStage?.color?.text : '#94a3b8'"
            [style.border-top]="isStageReached(stage) ? 'none' : '3px solid ' + stage.color?.background"
            [disabled]="movingStage"
            (click)="onSelectStage(stage)"
          >
            {{ stage.name }}
          </button>
        }
      </div>
    }
  </div>

  @if (isCardLoaded) {
    <div class="shrink-0 px-4 pt-4">
      <div
        class="rounded-lg border border-slate-200 bg-white px-4 shadow-sm [&_.p-tablist-tab-list]:!border-b-0"
      >
        <app-tabs-v2 [tabList]="detailTabs" (onSelectTab)="onSelectDetailTab($event)" />
      </div>
    </div>
  }

  <div class="min-h-0 flex-1 overflow-auto p-4">
    @if (isCardLoaded && isDealCard && activeDetailTab === CardDetailTab.PRODUCTS) {
      <app-board-v3-card-items
        [cardId]="cardId"
        [items]="boardService.card.items ?? []"
        [totals]="boardService.card.items_totals ?? []"
        [writtenOff]="boardService.card.items_written_off ?? false"
        [cardCurrency]="boardService.card.currency"
        [priceFromItems]="boardService.card.is_price_from_items ?? false"
        (changed)="onItemsChange($event)"
        (priceModeChange)="onPriceModeChange($event)"
      />
    } @else if (isCardLoaded && isDealCard && activeDetailTab === CardDetailTab.PAYMENTS) {
      <app-board-v3-card-payments
        [cardId]="cardId"
        [payments]="boardService.card.payments ?? []"
        [itemsTotals]="boardService.card.items_totals ?? []"
        [chats]="boardService.card.chats ?? []"
        [paidTotal]="boardService.card.paid_total ?? 0"
        [paymentStatus]="boardService.card.payment_status ?? null"
        [price]="boardService.card.price"
        [currency]="boardService.card.currency"
        (changed)="onPaymentsChange($event)"
      />
    } @else if (isCardLoaded && hasNovaPoshta && activeDetailTab === CardDetailTab.NOVA_POSHTA) {
      <app-board-v3-card-nova-poshta [cardId]="cardId" [card]="boardService.card" />
    } @else if (isCardLoaded) {
      <div class="grid grid-cols-1 gap-4 xl:grid-cols-[minmax(320px,0.42fr)_minmax(420px,0.58fr)]">
        <div class="flex min-w-0 flex-col gap-4">
          @if (sectionEditMode) {
            <div
              cdkDropList
              [cdkDropListAutoScrollStep]="12"
              (cdkDropListDropped)="onDropDraftSection($event)"
              class="grid gap-4"
            >
              @for (section of sectionDrafts; track section.id || $index; let sectionIndex = $index) {
                <section
                  cdkDrag
                  [cdkDragDisabled]="isMainSection(section)"
                  class="rounded-lg border border-orange-200 bg-white p-4 shadow-sm transition-transform duration-[260ms] ease-[cubic-bezier(0.2,0.8,0.2,1)]"
                >
                  <ng-template cdkDragPreview>
                    <div
                      class="flex min-h-[58px] w-[min(420px,calc(100vw-40px))] cursor-grabbing items-center gap-2.5 rounded-[10px] border border-orange-300 bg-white px-[18px] font-bold text-slate-800 shadow-[0_18px_45px_rgba(15,23,42,0.2),0_3px_10px_rgba(15,23,42,0.12)] [transform:rotate(1deg)_scale(1.015)]"
                    >
                      <app-icon class="!h-5 !w-5 !text-[20px] !leading-5">drag_indicator</app-icon>
                      <span>{{ section.name || ('section_name' | translate) }}</span>
                    </div>
                  </ng-template>
                  <ng-template cdkDragPlaceholder>
                    <div
                      class="min-h-[156px] animate-pulse rounded-[10px] border-2 border-dashed border-orange-300 bg-gradient-to-br from-orange-50 via-orange-100 to-amber-50"
                    ></div>
                  </ng-template>
                  <div class="mb-3 flex items-center gap-2 border-b border-slate-100 pb-3">
                    @if (!isMainSection(section)) {
                      <button
                        type="button"
                        cdkDragHandle
                        class="inline-flex h-8 w-8 shrink-0 cursor-grab items-center justify-center rounded text-slate-400 hover:bg-slate-100 hover:text-slate-700 active:cursor-grabbing"
                        [matTooltip]="'change_section_order' | translate"
                      >
                        <app-icon class="!h-5 !w-5 !text-[20px] !leading-5">drag_indicator</app-icon>
                      </button>
                    }
                    @if (isMainSection(section)) {
                      <h2 class="m-0 min-w-0 flex-1 truncate text-base font-semibold text-slate-950">
                        {{ section.name }}
                      </h2>
                    } @else {
                      <input
                        class="h-9 min-w-0 flex-1 rounded-lg border border-slate-200 px-3 text-base font-semibold text-slate-950 outline-none focus:border-orange-400"
                        [value]="section.name"
                        placeholder="{{ 'section_name' | translate }}"
                        (input)="updateDraftSectionName(sectionIndex, $any($event.target).value)"
                      />
                    }
                    @if (!isMainSection(section)) {
                      <button
                        type="button"
                        class="inline-flex h-8 w-8 shrink-0 items-center justify-center rounded text-slate-400 hover:bg-rose-50 hover:text-rose-600"
                        [matTooltip]="'delete_section' | translate"
                        (click)="removeDraftSection(sectionIndex)"
                      >
                        <app-icon class="!h-5 !w-5 !text-[20px] !leading-5">delete</app-icon>
                      </button>
                    }
                  </div>
                  <div
                    cdkDropList
                    [cdkDropListAutoScrollStep]="10"
                    (cdkDropListDropped)="onDropDraftSectionColumn($event, sectionIndex)"
                    class="grid gap-2"
                  >
                    @for (column of section.columns; track column.key; let columnIndex = $index) {
                      <div
                        cdkDrag
                        class="flex min-h-10 items-center gap-2 rounded-lg bg-slate-50 px-3 py-2 text-sm font-medium text-slate-800 transition-transform duration-[260ms] ease-[cubic-bezier(0.2,0.8,0.2,1)]"
                      >
                        <ng-template cdkDragPreview>
                          <div
                            class="flex min-h-10 w-[min(360px,calc(100vw-40px))] cursor-grabbing items-center gap-2.5 rounded-[10px] border border-orange-300 bg-white px-3 text-sm font-semibold text-slate-800 shadow-[0_18px_45px_rgba(15,23,42,0.2),0_3px_10px_rgba(15,23,42,0.12)] [transform:rotate(1deg)_scale(1.015)]"
                          >
                            <app-icon class="!h-4 !w-4 !text-[16px] !leading-4">drag_indicator</app-icon>
                            <span>{{ getSectionFieldLabel(column) }}</span>
                          </div>
                        </ng-template>
                        <ng-template cdkDragPlaceholder>
                          <div
                            class="min-h-10 animate-pulse rounded-lg border border-dashed border-orange-300 bg-orange-50"
                          ></div>
                        </ng-template>
                        <button
                          type="button"
                          cdkDragHandle
                          class="inline-flex cursor-grab text-slate-400 active:cursor-grabbing"
                        >
                          <app-icon class="!h-4 !w-4 !text-[16px] !leading-4">drag_indicator</app-icon>
                        </button>
                        <span class="min-w-0 flex-1 truncate">{{ getSectionFieldLabel(column) }}</span>
                        @if (column.key !== clientsKey) {
                          <span class="text-xs font-normal text-slate-400">{{ column.type }}</span>
                        }
                        <button
                          type="button"
                          class="inline-flex h-7 w-7 items-center justify-center rounded text-slate-400 hover:bg-rose-50 hover:text-rose-600"
                          [matTooltip]="'delete' | translate"
                          (click)="removeDraftSectionColumn(sectionIndex, columnIndex)"
                        >
                          <app-icon class="!h-4 !w-4 !text-[16px] !leading-4">close</app-icon>
                        </button>
                      </div>
                    }
                    <button
                      type="button"
                      class="flex min-h-10 items-center justify-center gap-1.5 rounded-lg! border border-dashed border-slate-200 px-3 text-sm font-medium text-slate-500 transition  hover:text-orange-600"
                      (click)="openSectionFieldsDialog(sectionIndex)"
                    >
                      <app-icon class="!h-4 !w-4 !text-[16px] !leading-4">add</app-icon>
                      {{ 'add_fields' | translate }}
                    </button>
                  </div>
                </section>
              }
            </div>
            <button
              type="button"
              class="flex min-h-11 items-center justify-center gap-1.5 rounded-lg! border border-dashed border-slate-200 bg-white px-4 text-sm font-medium text-slate-500 transition hover:border-orange-200 hover:bg-orange-50 hover:text-orange-600"
              (click)="addSection()"
            >
              <app-icon class="!h-4 !w-4 !text-[16px] !leading-4">add</app-icon>
              {{ 'create_section' | translate }}
            </button>
          } @else {
            @for (section of visibleSections; track section.id) {
              <section class="rounded-lg border border-slate-200 bg-white p-4 shadow-sm">
                <div class="mb-3 border-b border-slate-100 pb-3">
                  <h2 class="m-0 text-base font-semibold text-slate-950">{{ section.name }}</h2>
                </div>
                <div class="grid gap-3">
                  @for (column of section.visibleColumns; track column.key) {
                    @if (column.key === clientsKey) {
                      <div class="grid gap-1">
                        <div class="text-xs font-medium text-slate-500">
                          {{ 'board_v3_quick_create_clients' | translate }}
                        </div>
                        <div class="grid gap-2">
                          <app-board-v3-card-contacts
                            [cardId]="cardId"
                            [contacts]="boardService.card.contacts ?? []"
                            (contactsChange)="onContactsChange($event)"
                          />
                          <app-board-v3-card-company
                            [cardId]="cardId"
                            [company]="boardService.card.company ?? null"
                            (companyChange)="onCompanyChange($event)"
                          />
                        </div>
                      </div>
                    } @else {
                      <div class="grid gap-1">
                        @if (!isCheckboxField(column)) {
                          <div class="text-xs font-medium text-slate-500">
                            {{ getSectionFieldLabel(column) }}
                          </div>
                        }
                        @if (isCheckboxField(column) && isEditableSectionField(column)) {
                          @if (getCustomField(column); as field) {
                            <app-custom-field-control
                              [field]="field"
                              [control]="getSectionFieldControl(column)"
                              appearance="detail"
                              [showLabel]="false"
                            />
                          } @else {
                            <label
                              class="flex min-h-12 w-full cursor-pointer flex-row items-center gap-3 px-1 py-1"
                              style="display: flex; flex-direction: row"
                            >
                              <span class="flex h-10 w-16 shrink-0 items-center justify-center">
                                <p-toggleswitch
                                  [formControl]="getSectionFieldControl(column)"
                                  [attr.aria-label]="getSectionFieldLabel(column)"
                                />
                              </span>
                              <span class="min-w-0 flex-1 text-sm font-medium leading-5 text-slate-600">{{
                                getSectionFieldLabel(column)
                              }}</span>
                            </label>
                          }
                        } @else if (isAddressColumn(column)) {
                          @if (isEditableSectionField(column) && isSectionFieldEditing(section, column)) {
                            <div #editableControl data-card-detail-edit-trigger="true">
                              <app-board-v3-address-editor [formControl]="addressesControl" />
                            </div>
                          } @else {
                            <div class="flex flex-col gap-1.5">
                              @for (address of addressesControl.value; track $index) {
                                <div
                                  class="relative min-h-9 break-words rounded-lg bg-slate-50 px-3 py-2 pr-10 text-left text-sm font-medium text-slate-800"
                                  [class.cursor-text]="isEditableSectionField(column)"
                                  [attr.data-card-detail-edit-trigger]="isEditableSectionField(column) ? 'true' : null"
                                  (click)="editSectionField(section, column)"
                                >
                                  {{ formatCardAddress(address) }}
                                  <a
                                    class="absolute right-2 top-1/2 inline-flex h-7 w-7 -translate-y-1/2 items-center justify-center rounded-md !text-slate-400 !no-underline hover:!bg-slate-100 hover:!text-slate-900"
                                    [href]="getAddressMapUrl(address)"
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    [title]="'open_in_maps' | translate"
                                    (click)="$event.stopPropagation()"
                                    ><app-icon class="!h-4 !w-4 !text-[16px] !leading-none">location_on</app-icon></a
                                  >
                                </div>
                              } @empty {
                                <button
                                  type="button"
                                  class="min-h-9 w-full rounded-lg bg-slate-50 px-3 py-2 text-left text-sm font-medium text-slate-800"
                                  [class.cursor-text]="isEditableSectionField(column)"
                                  [disabled]="!isEditableSectionField(column)"
                                  [attr.data-card-detail-edit-trigger]="isEditableSectionField(column) ? 'true' : null"
                                  (click)="editSectionField(section, column)"
                                >
                                  -
                                </button>
                              }
                            </div>
                          }
                        } @else if (isPhoneColumn(column) || isEmailColumn(column)) {
                          @if (isEditableSectionField(column) && isSectionFieldEditing(section, column)) {
                            <div class="flex flex-col gap-2" data-card-detail-edit-trigger="true">
                              @for (control of getFieldArray(column).controls; track $index) {
                                <div class="flex items-center gap-2">
                                  <input
                                    #editableControl
                                    [type]="isPhoneColumn(column) ? 'tel' : 'email'"
                                    class="h-10 w-full rounded-lg border border-slate-200 bg-white px-3 text-sm font-medium text-slate-800 outline-none focus:border-orange-400"
                                    [class.border-red-400]="isPhoneColumn(column) && control.invalid && control.touched"
                                    [formControl]="control"
                                    (input)="isPhoneColumn(column) ? onPhoneInput(control, $event) : null"
                                  />
                                  @if (getFieldArray(column).length > 1) {
                                    <button
                                      type="button"
                                      class="inline-flex h-9 w-9 shrink-0 items-center justify-center rounded-lg text-slate-400 hover:bg-rose-50 hover:text-rose-600"
                                      (click)="removeFieldRow(column, $index)"
                                    >
                                      <app-icon class="!h-4 !w-4 !text-[16px] !leading-none">delete</app-icon>
                                    </button>
                                  }
                                </div>
                                @if (isPhoneColumn(column) && control.invalid && control.touched) {
                                  <p class="m-0 text-xs text-red-500">{{ 'input_errors.phone' | translate }}</p>
                                }
                              }
                              <button
                                type="button"
                                class="flex w-fit items-center gap-1 text-xs font-semibold text-orange-600 hover:text-orange-700"
                                (click)="addFieldRow(column)"
                              >
                                <app-icon class="!h-4 !w-4 !text-[16px] !leading-none">add</app-icon>
                                {{ (isPhoneColumn(column) ? 'add_phone' : 'add_email') | translate }}
                              </button>
                            </div>
                          } @else if (isEditableSectionField(column)) {
                            <div class="flex flex-col gap-1.5">
                              @for (value of getPhoneEmailDisplayValues(column); track $index) {
                                <div
                                  class="relative min-h-9 cursor-text break-all rounded-lg bg-slate-50 px-3 py-2 pr-10 text-left text-sm font-medium text-slate-800"
                                  data-card-detail-edit-trigger="true"
                                  (click)="editSectionField(section, column)"
                                >
                                  {{ value }}
                                  <a
                                    class="absolute right-2 top-1/2 inline-flex h-7 w-7 -translate-y-1/2 items-center justify-center rounded-md !text-slate-400 !no-underline hover:!bg-slate-100 hover:!text-slate-900"
                                    [href]="getPhoneEmailHref(column, value)"
                                    (click)="$event.stopPropagation()"
                                    ><app-icon class="!h-4 !w-4 !text-[16px] !leading-none">{{
                                      getPhoneEmailIcon(column)
                                    }}</app-icon></a
                                  >
                                </div>
                              } @empty {
                                <button
                                  type="button"
                                  class="min-h-9 w-full cursor-text rounded-lg bg-slate-50 px-3 py-2 text-left text-sm font-medium text-slate-800"
                                  data-card-detail-edit-trigger="true"
                                  (click)="editSectionField(section, column)"
                                >
                                  -
                                </button>
                              }
                            </div>
                          } @else {
                            <div class="flex flex-col gap-1.5">
                              @for (value of getPhoneEmailDisplayValues(column); track $index) {
                                <div
                                  class="min-h-9 break-all rounded-lg bg-slate-50 px-3 py-2 text-sm font-medium text-slate-800"
                                >
                                  {{ value }}
                                </div>
                              } @empty {
                                <div class="min-h-9 rounded-lg bg-slate-50 px-3 py-2 text-sm font-medium text-slate-800">
                                  -
                                </div>
                              }
                            </div>
                          }
                        } @else if (isEditableSectionField(column) && isSectionFieldEditing(section, column)) {
                          @if (column.key === 'price') {
                            <div
                              #editableControl
                              class="flex items-center gap-2"
                              data-card-detail-edit-trigger="true"
                              (focusout)="closeFieldOnFocusOut($event)"
                            >
                              <input
                                type="text"
                                inputmode="decimal"
                                class="h-10 min-w-0 flex-1 rounded-lg border border-slate-200 bg-white px-3 text-sm font-medium text-slate-800 outline-none focus:border-orange-400"
                                [class.border-red-400]="form.controls.price.invalid && form.controls.price.touched"
                                [formControl]="form.controls.price"
                                [attr.aria-label]="getSectionFieldLabel(column)"
                                (input)="onSystemPriceInput($event)"
                              />
                              <app-select
                                class="w-[124px] shrink-0 self-center"
                                [formControl]="form.controls.currency"
                                [options]="currencyOptions"
                                optionLabel="label"
                                optionValue="value"
                                [fluid]="false"
                                [appendTo]="'body'"
                                (pointerdown)="keepEditFocusForSelect()"
                                (opened)="keepEditFocusForSelect()"
                                (optionSelected)="keepEditFocusForSelect()"
                              />
                            </div>
                          } @else if (column.key === 'assigned_id') {
                            <div #editableControl data-card-detail-edit-trigger="true">
                              <app-select
                                [formControl]="getSectionFieldControl(column)"
                                [options]="managersOptions"
                                optionLabel="name"
                                optionValue="id"
                                [isAvatar]="true"
                                [showClear]="true"
                                [appendTo]="'body'"
                                (optionSelected)="onSectionSelectChange(column)"
                              />
                            </div>
                          } @else if (isDateField(column)) {
                            <div
                              #editableControl
                              class="grid grid-cols-2 gap-3"
                              data-card-detail-edit-trigger="true"
                              data-card-detail-skip-auto-focus="true"
                              (pointerdown)="keepEditFocusForOverlay()"
                              (focusout)="onSectionFieldBlur(column, $event)"
                            >
                              <div class="block">
                                <p-datepicker
                                  styleClass="w-full"
                                  [fluid]="true"
                                  dataType="string"
                                  dateFormat="yy-mm-dd"
                                  [showButtonBar]="true"
                                  [appendTo]="'body'"
                                  panelStyleClass="board-v3-datepicker-panel"
                                  inputStyleClass="h-10 w-full rounded-lg border border-slate-200 bg-white px-3 text-sm font-medium text-slate-800 outline-none focus:border-orange-400"
                                  [formControl]="getSectionFieldControl(column)"
                                  [ariaLabel]="getSectionFieldLabel(column)"
                                  (onShow)="keepEditFocusForOverlay()"
                                  (onMonthChange)="keepEditFocusForOverlay()"
                                  (onYearChange)="keepEditFocusForOverlay()"
                                  (onSelect)="onSectionDateChange(column); keepEditFocusForOverlay()"
                                  (onInput)="onSectionDateChange(column)"
                                  (onClear)="onSectionDateChange(column)"
                                />
                              </div>
                              <div class="block">
                                <p-datepicker
                                  styleClass="w-full"
                                  [fluid]="true"
                                  dataType="string"
                                  [timeOnly]="true"
                                  hourFormat="24"
                                  [stepMinute]="1"
                                  [appendTo]="'body'"
                                  panelStyleClass="board-v3-datepicker-panel"
                                  inputStyleClass="h-10 w-full rounded-lg border border-slate-200 bg-white px-3 text-sm font-medium text-slate-800 outline-none focus:border-orange-400 disabled:cursor-not-allowed disabled:bg-slate-50"
                                  [formControl]="getSectionDateTimeControl(column)"
                                  [disabled]="!getSectionFieldControl(column).value"
                                  [ariaLabel]="'time_label' | translate"
                                  (onShow)="keepEditFocusForOverlay()"
                                  (onSelect)="keepEditFocusForOverlay()"
                                />
                              </div>
                            </div>
                          } @else if (isEditableCustomField(column)) {
                            <div #editableControl>
                              <app-custom-field-control
                                [field]="getCustomField(column)!"
                                [control]="getSectionFieldControl(column)"
                                [dateTimeControl]="getSectionDateTimeControl(column)"
                                appearance="detail"
                                [showLabel]="false"
                                (blurred)="onCustomSectionFieldBlur(column)"
                                (dateChanged)="onSectionDateChange(column)"
                                (selectChanged)="onSectionSelectChange(column)"
                              />
                            </div>
                          } @else if (column.type === 'textarea') {
                            <div class="flex flex-col gap-1">
                              <textarea
                                #editableControl
                                rows="6"
                                [attr.maxlength]="getSectionFieldMaxLength(column)"
                                class="min-h-40 max-h-52 w-full resize-y overflow-y-auto rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm font-medium leading-6 text-slate-800 outline-none focus:border-orange-400"
                                [formControl]="getSectionFieldControl(column)"
                                (focusout)="onSectionFieldBlur(column, $event)"
                              ></textarea>
                              <span class="self-end text-xs font-medium text-slate-400">
                                {{ getSectionFieldValueLength(column) }}/{{ getSectionFieldMaxLength(column) }}
                              </span>
                            </div>
                          } @else {
                            <div class="flex flex-col gap-1">
                              <input
                                #editableControl
                                class="h-10 w-full rounded-lg border border-slate-200 bg-white px-3 text-sm font-medium text-slate-800 outline-none focus:border-orange-400"
                                [type]="getSectionInputType(column)"
                                [attr.inputmode]="
                                  isCustomNumberField(column) || isCustomPriceField(column)
                                    ? 'decimal'
                                    : isCustomPhoneField(column)
                                      ? 'tel'
                                      : isCustomEmailField(column)
                                        ? 'email'
                                        : isCustomLinkField(column)
                                          ? 'url'
                                          : null
                                "
                                [attr.maxlength]="getSectionFieldMaxLength(column)"
                                [formControl]="getSectionFieldControl(column)"
                                (input)="onSectionFieldInput(column, $event)"
                                (focusout)="onSectionFieldBlur(column, $event)"
                              />
                              <span class="self-end text-xs font-medium text-slate-400">
                                {{ getSectionFieldValueLength(column) }}/{{ getSectionFieldMaxLength(column) }}
                              </span>
                            </div>
                          }
                        } @else if (isCustomLinkField(column) && getSectionFieldValue(column) !== '-') {
                          <div class="relative">
                            <button
                              type="button"
                              class="min-h-9 w-full cursor-text break-all !rounded-lg bg-slate-50 px-3 py-2 pr-10 text-left text-sm font-medium text-slate-800"
                              data-card-detail-edit-trigger="true"
                              (click)="editSectionField(section, column)"
                            >
                              {{ getEditableSectionFieldValue(column) }}
                            </button>
                            <a
                              class="absolute right-2 top-1/2 inline-flex h-7 w-7 -translate-y-1/2 items-center justify-center rounded-md !text-slate-400 !no-underline hover:!bg-slate-100 hover:!text-slate-900"
                              [href]="getSectionLink(column)"
                              target="_blank"
                              rel="noopener noreferrer"
                              [title]="'open_link' | translate"
                              (click)="$event.stopPropagation()"
                              ><app-icon class="!h-4 !w-4 !text-[16px] !leading-none">open_in_new</app-icon></a
                            >
                          </div>
                        } @else if (isCustomPhoneField(column) && getSectionFieldValue(column) !== '-') {
                          <div class="relative">
                            <button
                              type="button"
                              class="min-h-9 w-full cursor-text break-all rounded-lg bg-slate-50 px-3 py-2 pr-10 text-left text-sm font-medium text-slate-800"
                              data-card-detail-edit-trigger="true"
                              (click)="editSectionField(section, column)"
                            >
                              {{ getEditableSectionFieldValue(column) }}
                            </button>
                            <a
                              class="absolute right-2 top-1/2 inline-flex h-7 w-7 -translate-y-1/2 items-center justify-center rounded-md !text-slate-400 !no-underline hover:!bg-slate-100 hover:!text-slate-900"
                              [href]="getSectionPhoneLink(column)"
                              [title]="'call' | translate"
                              (click)="$event.stopPropagation()"
                              ><app-icon class="!h-4 !w-4 !text-[16px] !leading-none">phone</app-icon></a
                            >
                          </div>
                        } @else if (isCustomEmailField(column) && getSectionFieldValue(column) !== '-') {
                          <div class="relative">
                            <button
                              type="button"
                              class="min-h-9 w-full cursor-text break-all !rounded-lg bg-slate-50 px-3 py-2 pr-10 text-left text-sm font-medium text-slate-800"
                              data-card-detail-edit-trigger="true"
                              (click)="editSectionField(section, column)"
                            >
                              {{ getEditableSectionFieldValue(column) }}
                            </button>
                            <a
                              class="absolute right-2 top-1/2 inline-flex h-7 w-7 -translate-y-1/2 items-center justify-center rounded-md !text-slate-400 !no-underline hover:!bg-slate-100 hover:!text-slate-900"
                              [href]="'mailto:' + getSectionFieldValue(column)"
                              [title]="'email' | translate"
                              (click)="$event.stopPropagation()"
                              ><app-icon class="!h-4 !w-4 !text-[16px] !leading-none">mail_outline</app-icon></a
                            >
                          </div>
                        } @else if (column.key === 'assigned_id' && isEditableSectionField(column)) {
                          <button
                            type="button"
                            class="flex h-10 w-full items-center gap-2 !rounded-lg bg-slate-50 px-3 text-left text-sm font-medium text-slate-800"
                            data-card-detail-edit-trigger="true"
                            (click)="editSectionField(section, column)"
                          >
                            <app-avatar
                              [url]="getAssignedManagerAvatarUrl(column)"
                              [title]="getEditableSectionFieldValue(column)"
                              size="24px"
                              [isPhotoGallery]="false"
                            />
                            <span class="min-w-0 flex-1 truncate">{{ getEditableSectionFieldValue(column) }}</span>
                          </button>
                        } @else if (isEditableSectionField(column)) {
                          <button
                            type="button"
                            class="w-full cursor-text !rounded-lg bg-slate-50 text-left text-sm font-medium text-slate-800"
                            data-card-detail-edit-trigger="true"
                            [ngClass]="
                              isTextareaField(column)
                                ? 'flex h-[8.5rem] items-start overflow-y-auto whitespace-pre-wrap px-3 py-2 leading-6'
                                : 'flex h-10 items-center overflow-x-auto whitespace-nowrap px-3'
                            "
                            [textContent]="getEditableSectionFieldValue(column)"
                            (click)="editSectionField(section, column)"
                          ></button>
                        } @else if (isCustomLinkField(column) && getSectionFieldValue(column) !== '-') {
                          <div
                            class="relative min-h-9 break-all rounded-lg bg-slate-50 px-3 py-2 pr-10 text-sm font-medium text-slate-800"
                          >
                            {{ getSectionFieldValue(column) }}
                            <a
                              class="absolute right-2 top-1/2 inline-flex h-7 w-7 -translate-y-1/2 items-center justify-center rounded-md !text-slate-400 !no-underline hover:!bg-slate-100 hover:!text-slate-900"
                              [href]="getSectionLink(column)"
                              target="_blank"
                              rel="noopener noreferrer"
                              [title]="'open_link' | translate"
                              ><app-icon class="!h-4 !w-4 !text-[16px] !leading-none">open_in_new</app-icon></a
                            >
                          </div>
                        } @else if (isCustomPhoneField(column) && getSectionFieldValue(column) !== '-') {
                          <div
                            class="relative min-h-9 break-all rounded-lg bg-slate-50 px-3 py-2 pr-10 text-sm font-medium text-slate-800"
                          >
                            {{ getSectionFieldValue(column) }}
                            <a
                              class="absolute right-2 top-1/2 inline-flex h-7 w-7 -translate-y-1/2 items-center justify-center rounded-md !text-slate-400 !no-underline hover:!bg-slate-100 hover:!text-slate-900"
                              [href]="getSectionPhoneLink(column)"
                              [title]="'call' | translate"
                              ><app-icon class="!h-4 !w-4 !text-[16px] !leading-none">phone</app-icon></a
                            >
                          </div>
                        } @else if (isCustomEmailField(column) && getSectionFieldValue(column) !== '-') {
                          <div
                            class="relative min-h-9 break-all rounded-lg bg-slate-50 px-3 py-2 pr-10 text-sm font-medium text-slate-800"
                          >
                            {{ getSectionFieldValue(column) }}
                            <a
                              class="absolute right-2 top-1/2 inline-flex h-7 w-7 -translate-y-1/2 items-center justify-center rounded-md !text-slate-400 !no-underline hover:!bg-slate-100 hover:!text-slate-900"
                              [href]="'mailto:' + getSectionFieldValue(column)"
                              [title]="'email' | translate"
                              ><app-icon class="!h-4 !w-4 !text-[16px] !leading-none">mail_outline</app-icon></a
                            >
                          </div>
                        } @else if (column.key === 'updated_by') {
                          <div
                            class="flex h-10 w-full items-center gap-2 rounded-lg bg-slate-50 px-3 text-sm font-medium text-slate-800"
                          >
                            <app-avatar
                              [url]="getUpdatedByAvatarUrl()"
                              [title]="getSectionFieldValue(column)"
                              size="24px"
                              [isPhotoGallery]="false"
                            />
                            <span class="min-w-0 flex-1 truncate">{{ getSectionFieldValue(column) }}</span>
                          </div>
                        } @else {
                          <div class="min-h-9 rounded-lg bg-slate-50 px-3 py-2 text-sm font-medium text-slate-800">
                            {{ getSectionFieldValue(column) }}
                          </div>
                        }
                        @if (
                          hasSectionFieldError(column) &&
                          !(isEditableCustomField(column) && isSectionFieldEditing(section, column))
                        ) {
                          <p class="text-xs text-red-500">{{ getSectionFieldError(column) }}</p>
                        }
                      </div>
                    }
                  }
                </div>
              </section>
            }
          }
        </div>

        <div class="flex min-w-0 flex-col gap-4">
          <app-board-v3-card-general-panel
            #generalPanel
            [cardId]="cardId"
            [cardName]="boardService.card.name"
            [funnelType]="entityType"
            [managers]="managersOptions"
            [chats]="boardService.card.chats ?? []"
            (commentAdded)="loadTimeline()"
            (reminderAdded)="loadTimeline()"
          />

          <app-timeline
            [entityType]="TIMELINE_ENTITY_TYPE.CARD"
            [entityId]="cardId"
            [filters]="TIMELINE_FILTERS"
            [fieldDefinitions]="fieldDefinitions"
            [cardCurrency]="boardService.card.currency"
            (repeatReminder)="generalPanel.prefillReminder($event)"
            (editReminder)="generalPanel.prefillReminderForEdit($event)"
            (contactChanged)="onTimelineContactChanged($event)"
            (companyChanged)="onTimelineCompanyChanged($event)"
          />
        </div>
      </div>
    } @else if (loading) {
      <div class="grid grid-cols-1 gap-4 xl:grid-cols-2">
        <div class="h-80 animate-pulse rounded-lg bg-white"></div>
        <div class="h-80 animate-pulse rounded-lg bg-white"></div>
      </div>
    } @else {
      <div class="flex h-full min-h-64 flex-col items-center justify-center rounded-lg bg-white px-4 py-10 text-center">
        <app-icon class="mb-2 !text-[32px] text-slate-300">error_outline</app-icon>
        <p class="m-0 text-sm font-medium text-slate-600">{{ 'failed_to_load_card' | translate }}</p>
        <app-button
          btnType="stroked"
          styleType="rounded"
          [customClass]="'mt-4 !h-10 min-w-[132px] !rounded-lg !px-5 text-sm font-semibold !leading-none'"
          (onChangeClick)="loadCard()"
        >
          {{ 'refresh_card' | translate }}
        </app-button>
      </div>
    }
  </div>

  <div
    class="shrink-0 border-t border-slate-200 bg-white px-5 py-3 shadow-[0_-4px_12px_rgba(15,23,42,0.04)]"
    [class.hidden]="!isCardLoaded"
  >
    <div class="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
      <div class="flex min-h-10 items-center gap-2 text-sm">
        @if (sectionEditMode) {
          <span class="h-2 w-2 shrink-0 rounded-full bg-orange-400"></span>
          <span class="font-medium text-orange-700">{{ 'editing_sections' | translate }}</span>
        } @else if (hasChanges) {
          <span class="h-2 w-2 shrink-0 rounded-full bg-amber-400"></span>
          <span class="font-medium text-amber-700">{{ 'unsaved_changes' | translate }}</span>
        } @else {
          <span class="h-2 w-2 shrink-0 rounded-full bg-emerald-500"></span>
          <span class="font-medium text-slate-500">{{ 'changes_saved' | translate }}</span>
        }
      </div>

      <div class="flex shrink-0 gap-2">
        @if (sectionEditMode) {
          <app-button
            btnType="stroked"
            styleType="rounded"
            [customClass]="'!h-10 min-w-[110px] !rounded-lg !px-5 text-sm font-semibold !leading-none'"
            [disabled]="savingSections"
            (onChangeClick)="cancelSectionEditMode()"
          >
            {{ 'cancel' | translate }}
          </app-button>
          <app-button
            styleType="rounded"
            [customClass]="'!h-10 min-w-[132px] !rounded-lg !bg-emerald-600 !px-5 text-sm font-semibold !leading-none hover:!bg-emerald-700 active:!bg-emerald-800 [--mat-text-button-hover-state-layer-opacity:0] [--mat-text-button-focus-state-layer-opacity:0] [--mat-text-button-pressed-state-layer-opacity:0] [&.btn-default.btn-default]:disabled:!bg-slate-200 [&.btn-default.btn-default]:disabled:!text-slate-400'"
            [disabled]="savingSections || !sectionDraftDirty"
            (onChangeClick)="saveSectionChanges()"
          >
            {{ (savingSections ? 'saving' : 'save') | translate }}
          </app-button>
        } @else {
          <app-button
            btnType="stroked"
            styleType="rounded"
            [customClass]="'!h-10 min-w-[132px] !rounded-lg !px-5 text-sm font-semibold !leading-none [&.btn-stroked.btn-stroked]:!border-slate-950 [&.btn-stroked.btn-stroked]:!text-slate-950 hover:[&.btn-stroked.btn-stroked]:!bg-slate-50 active:[&.btn-stroked.btn-stroked]:!bg-slate-100 [--mat-button-outlined-hover-state-layer-opacity:0] [--mat-button-outlined-focus-state-layer-opacity:0] [--mat-button-outlined-pressed-state-layer-opacity:0] [&.btn-stroked.btn-stroked]:disabled:!border-slate-200 [&.btn-stroked.btn-stroked]:disabled:!bg-white [&.btn-stroked.btn-stroked]:disabled:!text-slate-300'"
            [disabled]="saving || !hasChanges"
            (onChangeClick)="cancelChanges()"
          >
            {{ 'cancel' | translate }}
          </app-button>
          <app-button
            styleType="rounded"
            [customClass]="'!h-10 min-w-[132px] !rounded-lg !bg-emerald-600 !px-5 text-sm font-semibold !leading-none hover:!bg-emerald-700 active:!bg-emerald-800 [--mat-text-button-hover-state-layer-opacity:0] [--mat-text-button-focus-state-layer-opacity:0] [--mat-text-button-pressed-state-layer-opacity:0] [&.btn-default.btn-default]:disabled:!bg-slate-200 [&.btn-default.btn-default]:disabled:!text-slate-400'"
            [disabled]="saving || !hasChanges"
            (onChangeClick)="save()"
          >
            {{ (saving ? 'saving' : 'save') | translate }}
          </app-button>
        }
      </div>
    </div>
  </div>

  @if (conflict) {
    <div class="absolute inset-0 z-40 flex items-center justify-center bg-slate-950/35 p-5">
      <div class="w-full max-w-md rounded-lg border border-slate-200 bg-white p-5 shadow-2xl">
        <div class="flex items-start gap-3">
          <div class="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-sky-100 text-sky-700">
            <app-icon class="!h-5 !w-5 !text-[20px] !leading-5">sync_problem</app-icon>
          </div>
          <div class="min-w-0">
            <h2 class="m-0 text-base font-semibold text-slate-950">{{ 'board_v3_conflict_title' | translate }}</h2>
            <p class="mb-0 mt-1 text-sm leading-5 text-slate-600">
              {{ 'board_v3_conflict_description' | translate: { name: conflict.authorName } }}
            </p>
            @if (conflict.changes.length) {
              <p class="mb-0 mt-3 text-xs font-semibold uppercase tracking-wide text-slate-500">
                {{ 'board_v3_conflict_changed_fields' | translate }}
              </p>
              <ul class="mb-0 mt-1 flex flex-wrap gap-1.5 p-0">
                @for (change of conflict.changes; track change) {
                  <li class="list-none rounded-md bg-slate-100 px-2 py-1 text-xs font-medium text-slate-700">
                    {{ change }}
                  </li>
                }
              </ul>
            }
          </div>
        </div>

        <div class="mt-5 flex flex-col-reverse gap-2 sm:flex-row sm:justify-center">
          <app-button
            btnType="cancel"
            styleType="rounded"
            [customClass]="'!h-11 min-w-[148px] !rounded-lg !bg-rose-600 !px-5 text-sm font-semibold !leading-none hover:!bg-rose-700 active:!bg-rose-800 [--mat-text-button-hover-state-layer-opacity:0] [--mat-text-button-focus-state-layer-opacity:0] [--mat-text-button-pressed-state-layer-opacity:0]'"
            (onChangeClick)="discardMyChanges()"
          >
            {{ 'board_v3_conflict_discard_mine' | translate }}
          </app-button>
          <app-button
            styleType="rounded"
            [customClass]="'!h-11 min-w-[148px] !rounded-lg !bg-emerald-600 !px-5 text-sm font-semibold !leading-none hover:!bg-emerald-700 active:!bg-emerald-800 [--mat-text-button-hover-state-layer-opacity:0] [--mat-text-button-focus-state-layer-opacity:0] [--mat-text-button-pressed-state-layer-opacity:0]'"
            (onChangeClick)="keepMyChanges()"
          >
            {{ 'board_v3_conflict_keep_mine' | translate }}
          </app-button>
        </div>
      </div>
    </div>
  }

  @if (showCloseConfirmation) {
    <div class="absolute inset-0 z-30 flex items-center justify-center bg-slate-950/35 p-5">
      <div class="w-full max-w-md rounded-lg border border-slate-200 bg-white p-5 shadow-2xl">
        <div class="flex items-start gap-3">
          <div class="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-amber-100 text-amber-700">
            <app-icon class="!h-5 !w-5 !text-[20px] !leading-5">edit</app-icon>
          </div>
          <div class="min-w-0">
            <h2 class="m-0 text-base font-semibold text-slate-950">{{ 'unsaved_changes' | translate }}</h2>
            <p class="mb-0 mt-1 text-sm leading-5 text-slate-600">{{ 'save_changes_before_closing' | translate }}</p>
          </div>
        </div>

        <div class="mt-5 flex flex-col-reverse gap-2 sm:flex-row sm:justify-center">
          <app-button
            btnType="stroked"
            styleType="rounded"
            [customClass]="'!h-11 min-w-[132px] !rounded-lg !px-5 text-sm font-semibold !leading-none [&.btn-stroked.btn-stroked]:!border-slate-300 [&.btn-stroked.btn-stroked]:!text-slate-700 hover:[&.btn-stroked.btn-stroked]:!border-slate-400 hover:[&.btn-stroked.btn-stroked]:!bg-slate-50 active:[&.btn-stroked.btn-stroked]:!bg-slate-100 [--mat-button-outlined-hover-state-layer-opacity:0] [--mat-button-outlined-focus-state-layer-opacity:0] [--mat-button-outlined-pressed-state-layer-opacity:0] [&.btn-stroked.btn-stroked]:disabled:!border-slate-200 [&.btn-stroked.btn-stroked]:disabled:!bg-slate-100 [&.btn-stroked.btn-stroked]:disabled:!text-slate-400'"
            [disabled]="saving"
            (onChangeClick)="cancelClose()"
          >
            {{ 'cancel' | translate }}
          </app-button>
          <app-button
            btnType="cancel"
            styleType="rounded"
            [customClass]="'!h-11 min-w-[148px] !rounded-lg !bg-rose-600 !px-5 text-sm font-semibold !leading-none hover:!bg-rose-700 active:!bg-rose-800 [--mat-text-button-hover-state-layer-opacity:0] [--mat-text-button-focus-state-layer-opacity:0] [--mat-text-button-pressed-state-layer-opacity:0] [&.btn-cancel.btn-cancel]:disabled:!bg-slate-200 [&.btn-cancel.btn-cancel]:disabled:!text-slate-400'"
            [disabled]="saving"
            (onChangeClick)="discardChangesAndClose()"
          >
            {{ 'dont_save' | translate }}
          </app-button>
          <app-button
            styleType="rounded"
            [customClass]="'!h-11 min-w-[132px] !rounded-lg !bg-emerald-600 !px-5 text-sm font-semibold !leading-none hover:!bg-emerald-700 active:!bg-emerald-800 [--mat-text-button-hover-state-layer-opacity:0] [--mat-text-button-focus-state-layer-opacity:0] [--mat-text-button-pressed-state-layer-opacity:0] [&.btn-default.btn-default]:disabled:!bg-slate-200 [&.btn-default.btn-default]:disabled:!text-slate-400'"
            [disabled]="saving"
            (onChangeClick)="saveAndClose()"
          >
            {{ (saving ? 'saving' : 'save') | translate }}
          </app-button>
        </div>
      </div>
    </div>
  }
</div>
